/** Цвет в каналах: красный, зелёный, синий (0–255) и непрозрачность (0–1) */
type Rgba = [number, number, number, number];

/** Место, куда можно вставить правило: таблица стилей или группирующее правило (`@layer`, `@media`, `@supports`) */
type RuleContainer = CSSStyleSheet | CSSGroupingRule;

/** Правило-замена одного объявления со смешиванием цвета */
type MixFallback = {
  /** Правило с тем же селектором, вставленное следом за блоком `@supports` со смешиванием */
  rule: CSSStyleRule;
  property: string;
  priority: string;
  /** Переменная, цвет которой смешивается с прозрачным */
  variable: string;
  /** Доля цвета в смеси: проценты или выражение (`var(--tw-shadow-alpha)`) */
  amount: string;
};

/**
 * Объявление «цвет из переменной, смешанный с прозрачным»: свойство, переменная, доля цвета, важность.
 * Смешивания с currentcolor и с явным цветом сюда не попадают: первое нельзя вычислить одним цветом
 * на весь документ, второе сборка уже заменяет готовым цветом.
 */
const MIX_DECLARATION =
  /(?:^|;)\s*([\w-]+)\s*:\s*color-mix\(in [\w-]+,\s*var\((--[\w-]+)\)\s+([^,;]+?),\s*transparent\)\s*(!important)?/g;

const fallbacks: MixFallback[] = [];
const processedSheets = new WeakSet<CSSStyleSheet>();

/** Значения переменных на прошлом пересчёте — чтобы не переписывать правила, пока тема не сменилась */
let appliedSignature = '';
let scheduledFrame = 0;

const isColorMixCondition = (rule: CSSSupportsRule) => {
  const condition = rule.conditionText.trim();
  return condition.includes('color-mix(') && !condition.startsWith('not');
};

const readRules = (container: RuleContainer): CSSRuleList | null => {
  try {
    return container.cssRules;
  } catch {
    // Правила таблицы стилей с чужого адреса (шрифты) браузер читать не даёт
    return null;
  }
};

/** Вставляет замены для правил блока `@supports` сразу после него — в тот же слой и на то же место в каскаде */
const addFallbacksAfter = (supportsRule: CSSSupportsRule, container: RuleContainer) => {
  Array.from(supportsRule.cssRules).forEach((rule) => {
    if (!(rule instanceof CSSStyleRule)) return;
    const declarations = Array.from(rule.style.cssText.matchAll(MIX_DECLARATION));
    if (!declarations.length) return;

    const index = Array.prototype.indexOf.call(container.cssRules, supportsRule) + 1;
    try {
      container.insertRule(`${rule.selectorText}{}`, index);
    } catch {
      return;
    }
    const inserted = container.cssRules[index] as CSSStyleRule;
    declarations.forEach(([, property, variable, amount, important]) => {
      fallbacks.push({ rule: inserted, property, variable, amount, priority: important ? 'important' : '' });
    });
  });
};

const addFallbacks = (container: RuleContainer) => {
  const rules = readRules(container);
  if (!rules) return;
  // Копия списка: вставка замен сдвигает индексы живого списка правил
  Array.from(rules).forEach((rule) => {
    if (rule instanceof CSSSupportsRule && isColorMixCondition(rule)) {
      addFallbacksAfter(rule, container);
    } else if (rule instanceof CSSGroupingRule) {
      addFallbacks(rule);
    }
  });
};

/** Разбирает цвет в записи, которую отдаёт холст: `#rrggbb` или `rgba(r, g, b, a)` */
const parseCanvasColor = (color: string): Rgba | null => {
  const hex = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(color);
  if (hex) return [Number.parseInt(hex[1], 16), Number.parseInt(hex[2], 16), Number.parseInt(hex[3], 16), 1];
  const rgba = /^rgba\((\d+), (\d+), (\d+), ([\d.]+)\)$/.exec(color);
  if (rgba) return [Number(rgba[1]), Number(rgba[2]), Number(rgba[3]), Number(rgba[4])];
  return null;
};

/** Приводит значение переменной к каналам цвета; `null` — значение не цвет или зависит от элемента */
const createColorParser = () => {
  const context = document.createElement('canvas').getContext('2d');

  return (value: string): Rgba | null => {
    if (!context || !value || /currentcolor/i.test(value)) return null;
    // Нераспознанный цвет холст молча пропускает — тогда от разной исходной заливки получатся разные ответы
    context.fillStyle = '#000';
    context.fillStyle = value;
    const color = String(context.fillStyle);
    context.fillStyle = '#fff';
    context.fillStyle = value;
    return String(context.fillStyle) === color ? parseCanvasColor(color) : null;
  };
};

const toTranslucent = ([red, green, blue, alpha]: Rgba, amount: string) => {
  const percent = /^([\d.]+)%$/.exec(amount);
  if (percent) {
    return `rgba(${red}, ${green}, ${blue}, ${Math.round(alpha * Number(percent[1]) * 10) / 1000})`;
  }
  return `rgb(${red} ${green} ${blue} / calc(${amount} * ${alpha}))`;
};

const applyFallbacks = (parseColor: (value: string) => Rgba | null) => {
  const rootStyle = getComputedStyle(document.documentElement);
  const values = new Map<string, string>();
  fallbacks.forEach(({ variable }) => {
    if (!values.has(variable)) values.set(variable, rootStyle.getPropertyValue(variable).trim());
  });

  const signature = JSON.stringify([fallbacks.length, ...values]);
  if (signature === appliedSignature) return;
  appliedSignature = signature;

  const colors = new Map<string, Rgba | null>();
  values.forEach((value, variable) => colors.set(variable, parseColor(value)));
  fallbacks.forEach(({ rule, property, priority, variable, amount }) => {
    const color = colors.get(variable);
    // Цвет не вычислился — остаётся запасной цвет самой сборки, без прозрачности
    if (color) rule.style.setProperty(property, toTranslucent(color, amount), priority);
    else rule.style.removeProperty(property);
  });
};

/**
 * Полупрозрачные цвета из переменных темы для браузеров без color-mix() (Chrome до 111).
 *
 * Tailwind собирает `bg-primary/10` смешиванием цвета переменной с прозрачным внутри
 * `@supports (color: color-mix(...))`, а вне этого условия оставляет тот же цвет без прозрачности:
 * подсветка строки, плашки и затемнение под окном становятся сплошными и закрывают содержимое.
 * Такому браузеру следом за каждым смешиванием вставляется правило с тем же селектором и rgba():
 * цвет переменной вычисляется на корне документа и пересчитывается, когда на корне меняется тема —
 * класс или переменные. В браузерах с color-mix() ничего не делает.
 *
 * Переменные, заданные ниже корня (у облака реплики), и currentcolor так не вычислить — там остаётся
 * сплошной цвет; полупрозрачность от цвета текста рисуется слоем с `opacity`.
 */
export const installColorMixFallback = () => {
  if (typeof CSS === 'undefined' || CSS.supports('color', 'color-mix(in lab, red, red)')) return;

  const parseColor = createColorParser();
  const scheduleApply = () => {
    if (scheduledFrame) return;
    scheduledFrame = requestAnimationFrame(() => {
      scheduledFrame = 0;
      applyFallbacks(parseColor);
    });
  };

  const addSheet = (sheet: CSSStyleSheet | null) => {
    if (!sheet || processedSheets.has(sheet)) return;
    processedSheets.add(sheet);
    addFallbacks(sheet);
    applyFallbacks(parseColor);
  };

  const watchStyleElement = (node: Node) => {
    if (!(node instanceof HTMLLinkElement || node instanceof HTMLStyleElement)) return;
    if (node.sheet) addSheet(node.sheet);
    else node.addEventListener('load', () => addSheet(node.sheet), { once: true });
  };

  Array.from(document.head.children).forEach(watchStyleElement);
  new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach(watchStyleElement))).observe(
    document.head,
    { childList: true },
  );
  new MutationObserver(scheduleApply).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'style'],
  });
};
